import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUserRole } from '../utils/auth';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = getCurrentUserRole();
  const isAdmin = role === 'ADMIN';
  const canSeeReportes = role === 'ADMIN' || role === 'SUPERVISOR';
  const canManageTurnos = role === 'ADMIN' || role === 'SUPERVISOR';
  const [turnosOpen, setTurnosOpen] = useState<boolean>(() => location.pathname.startsWith('/turnos/'));
  const [sidebarVisibleDesktop, setSidebarVisibleDesktop] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => window.innerWidth <= 768);

  const isTurnosRoute = useMemo(() => location.pathname.startsWith('/turnos/'), [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setTurnosOpen(isTurnosRoute);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isTurnosRoute, isMobile]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileMenuOpen((prev) => !prev);
      return;
    }
    setSidebarVisibleDesktop((prev) => !prev);
  };

  const onNavigateItem = (group: 'TURNOS' | 'OTHER') => {
    if (group === 'TURNOS') {
      setTurnosOpen(true);
    } else {
      setTurnosOpen(false);
    }
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const appLayoutClassName = [
    'app-layout',
    !sidebarVisibleDesktop && !isMobile ? 'sidebar-hidden' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const sidebarClassName = ['sidebar', isMobile && mobileMenuOpen ? 'open-mobile' : '']
    .filter(Boolean)
    .join(' ');
  const showGlobalToggle = isMobile ? !mobileMenuOpen : true;
  const globalToggleClassName = [
    'sidebar-global-toggle',
    !isMobile && sidebarVisibleDesktop ? 'desktop-with-sidebar' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={appLayoutClassName}>
      {isMobile && mobileMenuOpen && <button className="sidebar-backdrop" aria-label="Cerrar menu" onClick={() => setMobileMenuOpen(false)} />}

      {showGlobalToggle && (
        <button
          className={globalToggleClassName}
          type="button"
          onClick={toggleSidebar}
          aria-label={isMobile ? 'Abrir menu' : sidebarVisibleDesktop ? 'Ocultar menu' : 'Mostrar menu'}
        >
          {isMobile ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : sidebarVisibleDesktop ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M14.5 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M9.5 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      )}

      <aside className={sidebarClassName}>
        <div className="sidebar-header">
          <span className="sidebar-title">Geo Asistencia</span>
          {isMobile && (
            <button
              className="sidebar-header-toggle"
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Cerrar menu"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <nav>
          {isAdmin && (
            <NavLink to="/relaciones" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
              Administracion Relaciones
            </NavLink>
          )}
          <NavLink to="/sitios" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
            Sitios
          </NavLink>
          <NavLink to="/agentes" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
            Legajos
          </NavLink>
          {isAdmin && (
            <NavLink to="/empresas" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
              Empresas
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/usuarios" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
              Usuarios
            </NavLink>
          )}
          <NavLink to="/registros" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
            Registros
          </NavLink>
          {canSeeReportes && (
            <>
              <NavLink to="/reportes" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
                Reportes
              </NavLink>
              <NavLink to="/reporte-rotativos" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
                Reporte Rotativos
              </NavLink>
              <NavLink to="/rotativos" onClick={() => onNavigateItem('OTHER')} className={({ isActive }) => isActive ? 'active' : ''}>
                Calendario Rotativos
              </NavLink>
              <NavLink
                to="/turnos/calculo-asistencia"
                onClick={() => onNavigateItem('OTHER')}
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                Calculo Asistencia
              </NavLink>
            </>
          )}
          {canManageTurnos && (
            <>
              <button
                type="button"
                className="sidebar-group-toggle"
                onClick={() => setTurnosOpen((prev) => !prev)}
                aria-expanded={turnosOpen}
              >
                <span>Turnos</span>
                <span className={`sidebar-group-arrow ${turnosOpen ? 'open' : ''}`}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M9.5 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
              <div className={`sidebar-submenu ${turnosOpen ? 'open' : ''}`}>
                  <NavLink to="/turnos/turno" onClick={() => onNavigateItem('TURNOS')} className={({ isActive }) => isActive ? 'active submenu-link' : 'submenu-link'}>
                    Turno
                  </NavLink>
                  <NavLink to="/turnos/ciclos" onClick={() => onNavigateItem('TURNOS')} className={({ isActive }) => isActive ? 'active submenu-link' : 'submenu-link'}>
                    Ciclos
                  </NavLink>
                  <NavLink to="/turnos/asignacion" onClick={() => onNavigateItem('TURNOS')} className={({ isActive }) => isActive ? 'active submenu-link' : 'submenu-link'}>
                    Asignacion
                  </NavLink>
                  <NavLink to="/turnos/asignacion-rotativos" onClick={() => onNavigateItem('TURNOS')} className={({ isActive }) => isActive ? 'active submenu-link' : 'submenu-link'}>
                    Asignacion Rotativos
                  </NavLink>
                  <NavLink to="/turnos/tipos-justificaciones" onClick={() => onNavigateItem('TURNOS')} className={({ isActive }) => isActive ? 'active submenu-link' : 'submenu-link'}>
                    Novedades
                  </NavLink>
                  <NavLink to="/turnos/asignacion-justificaciones" onClick={() => onNavigateItem('TURNOS')} className={({ isActive }) => isActive ? 'active submenu-link' : 'submenu-link'}>
                    Asignacion Novedades
                  </NavLink>
                  <NavLink to="/turnos/feriados" onClick={() => onNavigateItem('TURNOS')} className={({ isActive }) => isActive ? 'active submenu-link' : 'submenu-link'}>
                    Feriados
                  </NavLink>
              </div>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <button
            onClick={handleLogout}
            style={{ background: 'none', border: 'none', color: '#7f8c8d', cursor: 'pointer', fontSize: '0.82rem' }}
          >
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
